import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { IconSpriteModule } from '@personal-area/icon-sprite';
import { UnsubscribeController } from '@personal-area/global/common'

import { TitleService } from '../../services/title.service';

export interface BreadcrumbModel {
  url: string;
  label: string;
  queryParams?: Params;
}

@Component({
  selector: 'global-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    MatTooltipModule,
    IconSpriteModule,
  ],
  providers: [
    TitleService
  ]
})
export class BreadcrumbsComponent extends UnsubscribeController implements OnInit {

  @Input() set dynamicRoute(dynamicRoute: BreadcrumbModel) {
    if (dynamicRoute) {
      this._currentRoute = dynamicRoute;
      this._buildBreadcrumb();
    }
  }

  @Input() set dynamicTitle(dynamicTitle: string) {
    this.title = dynamicTitle;
    this._buildBreadcrumb();
  }

  @Input() isTitleVisible: boolean;
  @Input() alignCenterTitle = false;
  @Input() isRoot = false;
  @Input() canNavigateBackForced = false;

  @Input() tooltip: string = '';

  get title(): string {
    return this._title
  }

  set title(value: string) {
    this._title = value;
    this._titleService.setTitle(value, true);
  }

  public routeTitle = '';
  public canNavigateBack = false;
  public breadcrumbs: BreadcrumbModel[] = [];
  public breadcrumb!: BreadcrumbModel;

  private _title = '';
  private _currentRoute!: BreadcrumbModel;

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _titleService: TitleService,
    private _cdRef: ChangeDetectorRef,
  ) {
    super();
    this.isTitleVisible = true;
  }

  public ngOnInit(): void {
    this._buildBreadcrumb();

    this._router.events
      .pipe(
        filter(evt => evt instanceof NavigationEnd),
        takeUntil(this._unsubscribe),
      )
      .subscribe(() => this._buildBreadcrumb());
  }

  private _buildBreadcrumb(): void {
    const route = this._lastRoute;
    const data = route?.data;
    const routes = this._getRoutes(route);
    let path = '';
    let label: string;

    // if routes have not only parent route
    // we take prev title and combined path of parents
    if (routes.length > 1) {
      label = routes[0].parent?.data?.['title'];

      routes.reverse().slice(0, routes.length - 1).forEach(route => {
        path = path.concat(route.routeConfig!.path!, '/');
      });
    } else {
      // if routes have only parent route
      // we take parent title and empty path because we can't navigate back
      label = data['title'];
    }
    this.routeTitle = data['title'];

    // we check Input route data only for an undefined because we can get empty line
    this.breadcrumb = {
      label: this._currentRoute?.label !== undefined ? this._currentRoute.label : label,
      url: this._currentRoute?.url !== undefined ? this._currentRoute.url : `/${path}`,
    };
    if (this._currentRoute?.queryParams) {
      this.breadcrumb.queryParams = this._currentRoute.queryParams;
    }
    this.canNavigateBack = (routes.length > 1 && !!this.breadcrumb.url) || this.canNavigateBackForced;

    this._cdRef.detectChanges();
  }

  private _getRoutes(route: ActivatedRouteSnapshot, routes: ActivatedRouteSnapshot[] = []): ActivatedRouteSnapshot[] {
    if (route.routeConfig && route.routeConfig.path) {
      routes.push(route);
    }
    if (route.parent) {
      return this._getRoutes(route.parent, routes);
    } else {
      return routes;
    }
  }

  private get _lastRoute(): ActivatedRouteSnapshot {
    let route = this._activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

}
